import _ from 'underscore';

/*
 * Abstraction library for storing data in the browser. Handles all the actual
 * browser interaction for localStorage, sessionStorage and cookies.
 */
export default class AtlStorage {
  constructor() {
    /*
     * Create a storage prefix key to use for local/session storage
     * Use a reverse of the site host. ie: atlassian.com prefix will
     * be com.atlassian.angular.storage
     */
    var prefix = window.location.hostname
      .split('.')
      .reverse()
      .join('.') + '.bxp.storage',

      $window = window,
      browserSupport = {},
      stringify = JSON.stringify,

      supports = function (storageType: string) {
        if (_.has(browserSupport, storageType)) {
          return browserSupport[storageType];
        }
        if (_.contains(['localStorage', 'sessionStorage'], storageType)) {
          try {
            browserSupport[storageType] = typeof $window[storageType] === 'object' &&
              typeof $window[storageType].setItem === 'function' &&
              typeof $window[storageType].getItem === 'function' &&
              typeof $window[storageType].clear === 'function';
          } catch (e) {
            browserSupport[storageType] = false;
          }
        }
        return browserSupport[storageType];
      };

    /*
     * Abstraction over local and session storage. If write error occurs, catch it
     * then clear the storage and disable browserSupport property for that storage
     * type so any further attempts to write to that browser storage don't work.
     * If local/session storage is unsupported, and the stringified value really small
     * then attempt to save it to a cookie as a fallback mechanism
     */
    var setItem = function (storageType: string, key: string, value: string) {
      var storageKey = [prefix, key].join('.'),
        outputString = stringify(value);

      if (supports(storageType)) {
        try {
          $window[storageType].setItem(storageKey, outputString);
        } catch (e) {
          browserSupport[storageType] = false;
        }
      } else if (outputString.length < 150) {
        this.cookie.set(storageKey, value);
      }
    };

    /*
     * Read in the value of a given key from local/session storage.
     * Fallback to cookies instituted here as well should local/session be
     * unavailable
     */
    var getItem = function (storageType: string, key: string) {
      var storageKey = [prefix, key].join('.');
      if (supports(storageType)) {
        return JSON.parse($window[storageType].getItem(storageKey));
      } else {
        this.cookie.get(storageKey);
      }
    };

    /*
     * Wipe out an item from local/session storage with cookie fallback
    */
    var removeItem = function (storageType: string, key: string) {
      var storageKey = [prefix, key].join('.');
      if (supports(storageType)) {
        $window[storageType].removeItem(storageKey);
      } else {
        this.cookie.remove(storageKey);
      }
    };

    /*
     * Clear all local/session storage data for the app
     */
    var clear = function (storageType: string) {
      if (supports(storageType)) {
        $window[storageType].clear();
      }
    };

    /*
     * In-memory cache
     */
    var SESSION_STORAGE = {};
    var LOCAL_STORAGE = {
      timestamp: getItem('localStorage', 'timestamp')
    };

    if (LOCAL_STORAGE.timestamp && _.isNumber(LOCAL_STORAGE.timestamp)) {
      var timestamp = new Date(LOCAL_STORAGE.timestamp),
        ageInDays = (new Date() - timestamp) / 1000 / 60 / 60 / 24;
      if (ageInDays > 30) {
        clear('localStorage');
      }
    } else {
      LOCAL_STORAGE = { timestamp: Date.parse(new Date()) };
      setItem('localStorage', 'timestamp', LOCAL_STORAGE.timestamp);
    }

    /*
     * Public API
     */
    return {
      local: {
        set(key: string, value: any) {
          // don't add null or undefined, but 0 and '' and false are okay
          if (!value && value !== 0 && value !== '' && value !== false) {
            return false;
          }
          LOCAL_STORAGE[key] = value;
          setItem('localStorage', key, value);
        },
        get(key: string) {
          LOCAL_STORAGE[key] = getItem('localStorage', key);
          return LOCAL_STORAGE[key] || null;
        },
        remove(key: string) {
          delete LOCAL_STORAGE[key];
          removeItem('localStorage', key);
        },
        clear() {
          LOCAL_STORAGE = {};
          clear('localStorage');
        }
      },
      session: {
        set(key: string, value: any) {
          // don't add null or undefined, but 0 and '' and false are okay
          if (!value && value !== 0 && value !== '' && value !== false) {
            return false;
          }
          SESSION_STORAGE[key] = value;
          setItem('sessionStorage', key, value);
        },
        get(key: string) {
          SESSION_STORAGE[key] = getItem('sessionStorage', key);
          return SESSION_STORAGE[key] || null;
        },
        remove(key: string) {
          delete SESSION_STORAGE[key];
          removeItem('sessionStorage', key);
        },
        clear() {
          SESSION_STORAGE = {};
          clear('sessionStorage');
        }
      },
      cookie: {
        set(key: string, value: string, days: number) {
          let date = new Date();
          date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));

          if (!days) {
            days = 30;
          }
          let expires = "; expires=" + date.toUTCString();

          document.cookie = key + "=" + value + expires + "; path=/";
        },
        get(key: string) {
          const encodedName = encodeURIComponent(key).replace(/[\-\.\+\*]/g, "\\$&");
          const regex = new RegExp("(?:(?:^|.*;)\\s*" + encodedName + "\\s*\\=\\s*([^;]*).*$)|^.*$");
          return decodeURIComponent(document.cookie.replace(regex, "$1")) || null;
        },
        remove(key: string) {
          this.cookie.set(key, '', -1);
        }
      },
      support: browserSupport
    };
  }
}
